import { useRef, useEffect } from 'react'

/**
 * Animated starfield background component with falling dots
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render over the starfield
 * @returns {JSX.Element} Canvas-based starfield animation with children overlay
 */
export default function Stars({ children }) {
  let c1 = useRef<HTMLCanvasElement>(null)
  let c2 = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (!c1.current) return
    const canvas = c1.current
    const ctx = canvas.getContext('2d')
    const cw = window.innerWidth
    const ch = window.innerHeight
    canvas.width = cw
    canvas.height = ch

    const stars: Star[] = []
    const numColumns = Math.floor(cw / 20)
    const tailLength = 360

    class Star {
      x: number
      y: number
      speed: number
      tail: Array<{x: number, y: number, opacity: number}>
      active: boolean

      constructor(x: number) {
        this.x = x
        this.y = Math.random() * -500
        this.speed = (Math.random() * 0.25 + 0.5) * 6.75 // 3.375 to 5.0625
        this.tail = []
        this.active = false
        
        // Pre-populate tail with full length
        for (let i = 0; i < tailLength; i++) {
          this.tail.push({ x: this.x, y: this.y - i, opacity: 0 })
        }
      }

      update() {
        // Start falling randomly
        if (!this.active) {
          if (Math.random() < 0.001) this.active = true
          return
        }

        // Add current position to tail
        this.tail.unshift({ x: this.x, y: this.y, opacity: 1 })
        
        // Limit tail length and fade
        if (this.tail.length > tailLength) {
          this.tail.pop()
        }
        
        // Fade tail segments - slower fade for first 100, very slow for remaining
        this.tail.forEach((segment, i) => {
          if (i < 100) {
            // Slower fade for first 100 segments
            segment.opacity = 1 - (i / 100) * 0.6
          } else {
            // Very slow fade for remaining segments
            const remainingIndex = i - 100
            const remainingLength = tailLength - 100
            segment.opacity = 0.4 - (remainingIndex / remainingLength) * 0.4
          }
        })

        // Move star
        this.y += this.speed

        // Reset when off screen (accounting for tail length)
        if (this.y > ch + tailLength) {
          this.y = Math.random() * -500
          this.speed = (Math.random() * 0.25 + 0.5) * 6.75
          this.tail = []
          this.active = false
          
          // Re-populate tail with full length
          for (let i = 0; i < tailLength; i++) {
            this.tail.push({ x: this.x, y: this.y - i, opacity: 0 })
          }
        }
      }

      draw() {
        if (!this.active) return

        // Draw tail
        this.tail.forEach(segment => {
          if (segment.opacity > 0) {
            ctx!.fillStyle = `rgba(0, 255, 255, ${segment.opacity * 0.6})`
            ctx!.fillRect(segment.x - 1, segment.y - 1, 2, 2)
          }
        })

        // Draw main star
        ctx!.fillStyle = '#00ffff'
        ctx!.fillRect(this.x - 1, this.y - 1, 2, 2)
      }
    }

    // Create stars
    for (let i = 0; i < numColumns; i++) {
      stars.push(new Star(i * 20 + 10))
    }

    function animate() {
      // Clear canvas
      ctx!.fillStyle = 'rgba(0, 0, 0, 1)'
      ctx!.fillRect(0, 0, cw, ch)

      // Update and draw stars
      stars.forEach(star => {
        star.update()
        star.draw()
      })

      requestAnimationFrame(animate)
    }

    animate()
  }, [c1])

  return (
    <div x-component-name="Stars">
      <canvas ref={c1} id='canvas' className='absolute top-0 left-0 block rotate-180 pointer-events-none'>
        Canvas is not supported in your browser.
      </canvas>
      <div className='relative z-10 text-white'>{children}</div>
    </div>
  )
}
